import { FC, useRef, useMemo, useState, useEffect, ReactNode } from 'react'
import {
  Paper,
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Snackbar,
  Alert,
  TableContainer,
  Table,
  TableBody,
  styled,
} from '@mui/material'
import EditIcon from '@/assets/icons/actions/edit.svg'
import ViewIcon from '@/assets/icons/actions/view.svg'
import { useVirtualizedTable } from '@/hooks/shared/useVirtualizedTable'
import {
  TableWrapper,
  TableContainerStyled,
  VirtualTableContainer,
  VirtualRow,
  VirtualCell,
} from '@/components/table/StyledTableComponents'
import { TableHeader } from '@/components/table/TableHeader'
import { TableFooter } from '@/components/table/TableFooter'
import { NoResultsAlert } from '@/components/notification/NoResultsAlert'
import type { SortableTableProps } from '@/utilities/types/table-props'
import type {
  TableColumn,
  AreaControllerData,
  AreaData,
  AllAreasType,
} from '@/utilities/types/table'
import DateFormatter from '@/utilities/date-formatter'
import ViewAreaControllerModal from '@/components/modal/area-management/area-controllers/ViewAreaControllerModal'
import { AreaChipsDisplay } from '@/components/modal/area-management/area-controllers/ViewAreaCollumnChips'
import { AssignAreaModal } from '@/components/modal/area-management/area-controllers/AssignAreaModal'
import { assignAreaController } from '@/api'
import { useAreaControllerVisualizedTable } from '@/hooks/shared/useAreaControllerVisualizedTable'
import { AreaControllerTableFooter } from '../../AreaControllerTableFooter'
import dayjs from 'dayjs'
import { MaintenanceAdminTableFooter } from '../../MaintenanceAdminTableFooter'

export const StyledRow = styled(Box)(() => ({
  display: 'flex',
  width: '100%',
  height: '52px',
  padding: '16px',
  '&:nth-of-type(even)': {
    backgroundColor: '#f5f5f5',
  },
  '&:nth-of-type(odd)': {
    backgroundColor: 'white',
  },
}))

export const StyledCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  wordBreak: 'break-word',
  textAlign: 'center',
  fontSize: '12px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
  },
}))

const columns: TableColumn[] = [
  { id: 'sourceUserId', label: 'ID', width: '10%', sortable: true },
  { id: 'user', label: 'Name', width: '25%', sortable: false },
  { id: 'areas', label: 'Areas', width: '25%', sortable: false },
  { id: 'dateCreated', label: 'Created Date', width: '15%', sortable: true },
  { id: 'dateModified', label: 'Modified Date', width: '15%', sortable: true },
  { id: 'action', label: 'Action', width: '10%', align: 'center' },
]

interface AreaControllerTableProps
  extends SortableTableProps<AreaControllerData> {
  onEdit?: (id: number) => void
  areas: AllAreasType[]
  onAreaAssigned?: () => Promise<void>
  refreshTable: () => Promise<void>
  handleSort: (field: string, direction: 'asc' | 'desc') => void
}

export const AreaManagementMaintenanceAdminTable: FC<
  AreaControllerTableProps
> = ({
  data = [],
  filterState,
  isLoading,
  error,
  onSort,
  areas = [],
  onAreaAssigned,
  refreshTable,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  totalElem,
  handleSort,
}) => {
  const parentRef = useRef<HTMLDivElement>(null)
  const [showNoResults, setShowNoResults] = useState(false)
  const [viewModalOpen, setViewModalOpen] = useState(false)
  const [assignModalOpen, setAssignModalOpen] = useState(false)
  const [selectedController, setSelectedController] =
    useState<AreaControllerData | null>(null)
  const [assignFormData, setAssignFormData] = useState({
    areaController: '',
    userAreas: [],
  })
  const [isAssigning, setIsAssigning] = useState(false)
  const [assignError, setAssignError] = useState<string | null>(null)

  // Add snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>(
    'success',
  )
  console.log('maintenance admin table in here', page)
  const {
    virtualRows,
    totalSize,
    paginatedItems,
    handlePageChange,
    handleRowsPerPageChange,
    handleVirtualizedScroll,
  } = useAreaControllerVisualizedTable({
    data,
    filterState,
    containerRef: parentRef,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    totalElem,
  })

  useEffect(() => {
    if (data.length <= 0 && isLoading) {
      setShowNoResults(true)
    }
  }, [data.length])

  const handleView = (controller: AreaControllerData) => {
    setSelectedController(controller)
    setViewModalOpen(true)
  }

  console.log('this is the page', page)

  const handleAssign = (controller: AreaControllerData) => {
    setSelectedController(controller)

    setAssignFormData({
      areaController: `${controller.firstName} ${controller.lastName}`,
      userAreas: controller.userAreas || [],
    })

    setAssignModalOpen(true)
  }

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false)
  }

  const handleAssignSubmit = async (formData: {
    areaController: string
    userAreas: []
  }) => {
    if (!selectedController) return

    setIsAssigning(true)
    setAssignError(null)

    try {
      const parsedIds = formData.userAreas.map((a) => a.area?.id ?? a.id)
      await assignAreaController({
        userID: selectedController.sourceUserId,
        areaIDs: parsedIds,
      })

      // Clear modal
      setAssignModalOpen(false)
      setSelectedController(null)
      setAssignFormData({
        areaController: '',
        userAreas: [],
      })

      setSnackbarMessage('Areas assigned successfully')
      setSnackbarSeverity('success')
      setSnackbarOpen(true)

      if (onAreaAssigned) await onAreaAssigned()
      await refreshTable()
    } catch (error) {
      setAssignError(
        error instanceof Error ? error.message : 'Failed to assign areas',
      )
      setSnackbarMessage('Failed to assign areas')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    } finally {
      setIsAssigning(false)
    }
  }
  if (isLoading) {
    return (
      <Box
        sx={{
          height: '70vh',
          width: '100%',
          background: 'white',
          zIndex: 1,
          display: 'fleex',
          justifyContent: 'center',
          alignItems: 'center',
          borderBottomLeftRadius: '10px',
          borderBottomRightRadius: '10px',
          marginTop: '2px',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>
        Error: {error instanceof Error ? error.message : 'Unknown error'}
      </Box>
    )
  }

  return (
    <div
      style={{
        boxShadow: 'none',
        width: '100%',
        zIndex: 1,
        marginTop: '2px',
      }}
    >
      <TableContainer component={Paper}>
        <Table
          sx={{ minWidth: 650, minHeight: '600px' }}
          aria-label="simple table"
        >
          <TableHeader
            columns={columns}
            filterState={filterState}
            onSort={onSort}
            handleSort={handleSort}
          />
          <TableBody
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            {paginatedItems?.map((row, index) => (
              <StyledRow key={index}>
                {columns.map((column) => {
                  if (column.id === 'sourceUserId') {
                    return (
                      <StyledCell style={{ width: column.width }}>
                        {row.sourceUserId}
                      </StyledCell>
                    )
                  }
                  if (column.id === 'user') {
                    return (
                      <StyledCell width={column.width}>
                        {`${row.firstName} ${row.lastName}`}
                      </StyledCell>
                    )
                  }

                  if (column.id === 'areas') {
                    if (Array.isArray(row.userAreas)) {
                      return (
                        <StyledCell width={column.width}>
                          <AreaChipsDisplay areas={row.userAreas} />
                        </StyledCell>
                      )
                    } else {
                      return <StyledCell>'-'</StyledCell>
                    }
                  }

                  if (column.id === 'dateCreated') {
                    return (
                      <StyledCell width={column.width}>
                        {dayjs(row.dateCreated).format('MMM DD, YYYY')}
                      </StyledCell>
                    )
                  }
                  if (column.id === 'dateModified') {
                    return (
                      <StyledCell width={column.width}>
                        {dayjs(row.dateModified).format('MMM DD, YYYY')}
                      </StyledCell>
                    )
                  }
                  if (column.id === 'action') {
                    return (
                      <StyledCell width={column.width}>
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="center"
                        >
                          <IconButton
                            onClick={() => handleView(row)}
                            sx={{
                              padding: '4px',
                              '&:hover': { bgcolor: 'rgba(73, 90, 255, 0.05)' },
                            }}
                          >
                            <img
                              src={ViewIcon}
                              alt="View"
                              width={16}
                              height={16}
                            />
                          </IconButton>
                          <IconButton
                            onClick={() => handleAssign(row)}
                            sx={{
                              padding: '4px',
                              '&:hover': { bgcolor: 'rgba(73, 90, 255, 0.05)' },
                            }}
                          >
                            <img
                              src={EditIcon}
                              alt="Assign"
                              width={16}
                              height={16}
                            />
                          </IconButton>
                        </Stack>
                      </StyledCell>
                    )
                  }
                })}
              </StyledRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ViewAreaControllerModal
        open={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false)
          setSelectedController(null)
        }}
        data={selectedController}
      />
      {selectedController && (
        <AssignAreaModal
          open={assignModalOpen}
          onClose={() => {
            setAssignModalOpen(false)
            setSelectedController(null)
            setAssignError(null)
          }}
          onSubmit={handleAssignSubmit}
          formData={assignFormData}
          setFormData={setAssignFormData}
          areas={areas}
          existingAreas={selectedController.areas || []}
          areaControllerName={`${selectedController.firstName} ${selectedController.lastName}`}
          isLoading={isAssigning}
          error={assignError}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <MaintenanceAdminTableFooter
        rowsPerPage={rowsPerPage}
        totalCount={totalElem}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </div>
  )
}
